import { Fallback, Image, Root } from "@radix-ui/react-avatar";
import { type VariantProps, cva } from "class-variance-authority";
import {
  type ComponentPropsWithoutRef,
  type ElementRef,
  forwardRef,
} from "react";

import { cn } from "@/lib/cn";

const avatarVariants = cva("relative flex h-10 w-10 shrink-0 overflow-hidden", {
  variants: {
    shape: {
      circle: "rounded-full",
      square: "rounded-md",
    },
    variant: {
      icon: "bg-muted",
      image: "",
    },
  },
  defaultVariants: {
    shape: "circle",
    variant: "icon",
  },
});

type AvatarProps = ComponentPropsWithoutRef<typeof Root> &
  VariantProps<typeof avatarVariants>;

const Avatar = forwardRef<ElementRef<typeof Root>, AvatarProps>(
  ({ className, shape, variant, ...props }, ref) => (
    <Root
      ref={ref}
      className={cn(avatarVariants({ shape, variant }), className)}
      {...props}
    />
  ),
);
Avatar.displayName = Root.displayName;

const AvatarImage = forwardRef<
  ElementRef<typeof Image>,
  ComponentPropsWithoutRef<typeof Image>
>(({ className, ...props }, ref) => (
  <Image
    ref={ref}
    className={cn("aspect-square h-full w-full object-contain", className)}
    data-avatar="true"
    {...props}
  />
));
AvatarImage.displayName = Image.displayName;

const AvatarFallback = forwardRef<
  ElementRef<typeof Fallback>,
  ComponentPropsWithoutRef<typeof Fallback>
>(({ className, ...props }, ref) => (
  <Fallback
    ref={ref}
    className={cn(
      "mx-1 flex h-full w-full items-center justify-center text-center leading-none",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = Fallback.displayName;

export { Avatar, AvatarFallback, AvatarImage };
